<template>
  <div>
    <v-combobox
      :model-value="selectedReadModel"
      @update:focused="onFocusChange"
      @update:modelValue="updateReadModel"
      @update:menu="onMenuChange"
      :id="`rm-combobox-input-${Math.random()}`"
      clear-on-select
      persistent-hint
      persistent-placeholder
      :hide-no-data="false"
      :no-data-text="openCreateStep ? '' : 'No read models or domain events available'"
      :menu-icon="selectedReadModel && typeof selectedReadModel === 'object' ? '$dropdown' : 'mdi-link'"
      density="compact"
      variant="outlined"
      :custom-filter="filterFunction"
      :items="combinedItems"
      item-value="id"
      item-title="name"
      :disabled="store.state.isWorkflowDisabled"
      ref="combobox"
      :style="{ '--inputMinWidth': inputMinWidth + 'px' }"
      class="entity-combobox"
      placeholder="Select schema"
    >
      <template #selection="{ item }">
        <v-chip
          v-if="selectedReadModel && typeof selectedReadModel === 'object'"
          density="compact"
          class="mr-2"
          variant="flat"
          :style="{ backgroundColor: item.raw?.type === SchemaType.DOMAIN_EVENT ? domainEventBgColor : readModelBgColor, color: 'var(--text-dark)' }"
        >
          <v-icon size="14" class="mr-1">{{ item.raw?.type === SchemaType.DOMAIN_EVENT ? 'mdi-note-outline' : 'mdi-eye-outline' }}</v-icon>
          {{ item.title }}
        </v-chip>
        <span v-else-if="showCustomValue" class="text-no-wrap" ref="selectionElement">
          {{ item.title }}
        </span>
      </template>

      <template #item="{ item, props: itemProps }">
        <template v-if="!openCreateStep">
          <v-list-subheader v-if="item.raw._isFirstInGroup && item.raw._group === 'readModel'" :style="{ backgroundColor: readModelBgColor }">
            <v-icon size="14" class="mr-1">mdi-eye-outline</v-icon> Read Models
          </v-list-subheader>
          <v-list-subheader v-if="item.raw._isFirstInGroup && item.raw._group === 'domainEvent'" :style="{ backgroundColor: domainEventBgColor }">
            <v-icon size="14" class="mr-1">mdi-note-outline</v-icon> Domain Events
          </v-list-subheader>
          <v-list-item v-bind="itemProps" />
        </template>
      </template>
    </v-combobox>

    <v-tooltip
      v-if="typeof selectedReadModel !== 'object' && !store.state.isWorkflowDisabled && tooltipActivatorEl"
      location="top"
      open-delay="700"
      :z-index="100000"
      :activator="tooltipActivatorEl"
      content-class="select-entity-combobox-tooltip"
    >
      <span>Not linked to a read model or domain event yet, click to select one.</span>
    </v-tooltip>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useStore } from 'vuex'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import { SchemaType } from '@/constants/schema'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import { sanitizeCardDescription } from '@/utils/sanitize'

const { events, cardIds } = defineProps({
  events: Array,
  cardIds: Array
})

const emit = defineEmits(['selectedSchemaType'])

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const queryCardType = computed(() => diagramHelpers.methods.getQueryCardType(cardTypes.value))
const domainEventCardType = computed(() => diagramHelpers.methods.getDomainEventCardType(cardTypes.value))
const readModelBgColor = computed(() => {
  const c = queryCardType.value?.color
  return c ? `rgba(${c.red},${c.green},${c.blue},0.15)` : 'transparent'
})
const domainEventBgColor = computed(() => {
  const c = domainEventCardType.value?.color
  return c ? `rgba(${c.red},${c.green},${c.blue},0.15)` : 'transparent'
})
const readModels = computed(() => {
  return (workflow.value?.schemas ?? []).filter((schema) => schema.type === SchemaType.QUERY)
})
const domainEvents = computed(() => {
  return (workflow.value?.schemas ?? []).filter((schema) => schema.type === SchemaType.DOMAIN_EVENT)
})
const combinedItems = computed(() => {
  const items = []
  readModels.value.forEach((rm, i) => {
    items.push({ ...rm, _group: 'readModel', _isFirstInGroup: i === 0 })
  })
  domainEvents.value.forEach((de, i) => {
    items.push({ ...de, _group: 'domainEvent', _isFirstInGroup: i === 0 })
  })
  return items
})

const openCreateStep = ref(false)
const selectedReadModel = ref(null)
const showCustomValue = ref(true)
const selectionElement = ref(null)
const inputMinWidth = ref(0)
const combobox = useTemplateRef('combobox')
const tooltipActivatorEl = ref(null)
let tooltipObserver = null

const updateTooltipActivatorEl = async () => {
  await nextTick()

  const el = combobox.value?.$el
  if (!el) {
    tooltipActivatorEl.value = null
    return
  }

  const linkIconEl = el.querySelector('[class*="mdi-link"], .mdi-link')
  const clickable = linkIconEl?.closest('button, [role="button"], .v-btn') || linkIconEl
  tooltipActivatorEl.value = clickable || null
}

const filterFunction = (_, query, item) => {
  return typeof selectedReadModel.value === 'string' && query === selectedReadModel.value
    ? true
    : item.title.toLowerCase().indexOf(query.toLowerCase()) > -1
}

const onFocusChange = (focused) => {
  if (!combobox.value) {
    return
  }

  if (focused) {
    const parentWidth = selectionElement.value?.offsetParent?.getBoundingClientRect()?.width - 11 || 0
    if (typeof selectedReadModel.value === 'string') {
      inputMinWidth.value = parentWidth
      combobox.value.search = selectedReadModel.value
    } else {
      inputMinWidth.value = 64
    }
  } else {
    inputMinWidth.value = 0
  }

  showCustomValue.value = !focused
}

const onMenuChange = (isOpen) => {
  if (!isOpen && openCreateStep.value) {
    openCreateStep.value = false
  }
}

const linkReadModelToCard = async (readModelSchema) => {
  const updatedSvgGraph = safeStructuredClone(svgGraph.value)
  const isStringValue = !readModelSchema || typeof readModelSchema === 'string'

  events.forEach((event) => {
    updatedSvgGraph[event.id].cards = updatedSvgGraph[event.id].cards?.map((item) => {
      const isTargetCard = cardIds.includes(item.id)

      if (!isTargetCard) {
        return item
      }

      return {
        ...item,
        schemaId: isStringValue ? null : readModelSchema.id,
        description: isStringValue ? (readModelSchema || '') : item.description
      }
    })
  })

  await workflowApi.updateWorkflow({
    svgGraph: updatedSvgGraph,
    workflow: workflow.value,
    updateNote: {
      action: readModelSchema?.type === SchemaType.DOMAIN_EVENT ? 'linked domain event' : 'linked read model',
      target: ''
    }
  }, true)
}

const updateReadModel = async (readModel) => {
  selectedReadModel.value = readModel

  if (!readModel) {
    return
  }

  if (combobox.value) {
    combobox.value.blur()
  }

  await linkReadModelToCard(readModel)
}

watch([readModels, domainEvents, () => events], ([newReadModels, newDomainEvents, newEvents]) => {
  if (!newEvents?.length) {
    return
  }
  const card = newEvents[0].cards?.find((item) => cardIds.includes(item.id))
  if (!card) {
    selectedReadModel.value = ''

    emit('selectedSchemaType', null)
    return
  }

  if (card.schemaId) {
    const schema = newReadModels.find((rm) => rm.id === card.schemaId) ||
      newDomainEvents.find((de) => de.id === card.schemaId)
    selectedReadModel.value = schema || sanitizeCardDescription(card.description || '')
    emit('selectedSchemaType', schema?.type || null)
  } else {
    selectedReadModel.value = sanitizeCardDescription(card.description || '')
    emit('selectedSchemaType', null)
  }
}, { immediate: true })

watch([selectedReadModel, () => store.state.isWorkflowDisabled], () => {
  updateTooltipActivatorEl()
})

onMounted(() => {
  updateTooltipActivatorEl()

  const el = combobox.value?.$el
  if (!el || typeof MutationObserver === 'undefined') {
    return
  }

  tooltipObserver = new MutationObserver(() => updateTooltipActivatorEl())
  tooltipObserver.observe(el, { childList: true, subtree: true })
})

onBeforeUnmount(() => {
  tooltipObserver?.disconnect?.()
  tooltipObserver = null
})
</script>
